<template>
  <div>
    <v-dialog
      v-model="dialogProxy"
      max-width="1600px"
      persistent
      scrollable
    >
      <BaseSnackbarUI
        v-model="uSnackbar.show"
        :text="uSnackbar.text"
        :color="uSnackbar.color"
        :timeout="uSnackbar.timeout"
        :bottom="false"
        :top="true"
        :right="false"
      ></BaseSnackbarUI>
      <v-card>
        <v-card-title class="headline d-flex align-center">
          <span>
            {{ isEditMode ? 'Edit delivery note' : 'Create delivery note' }}
          </span>
        
          <v-spacer />
        
          <v-chip
            v-if="form.status"
            :color="resolveStatusColor(form.status)"
            dark
          >
            {{ form.status }}
          </v-chip>
        
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      
        <v-divider />
      
        <v-card-text class="pa-0">
          <v-container fluid class="pa-6">
            <v-row>
              <v-col cols="12" md="3">
                <v-menu
                  v-model="documentDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.document_date"
                      label="Issue date"
                      readonly
                      outlined
                      v-bind="attrs"
                      v-on="on"
                      :class="{ 'optional-warning-field': hasOptionalWarning('document_date') }"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.document_date"
                    scrollable
                    no-title
                    @input="onDocumentDateSelected"
                  />
                </v-menu>
              </v-col>
            
              <v-col cols="12" md="3">
                <v-menu
                  v-model="deliveryDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.delivery_date"
                      label="Delivery date"
                      readonly
                      outlined
                      v-bind="attrs"
                      v-on="on"
                      :class="{ 'optional-warning-field': hasOptionalWarning('delivery_date') }"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.delivery_date"
                    scrollable
                    no-title
                    @input="onDeliveryDateSelected"
                  />
                </v-menu>
              </v-col>
            
              <v-col cols="12" md="3">
                <v-text-field
                  :value="form.number"
                  label="Waybill number"
                  outlined
                  readonly
                />
              </v-col>
            
              <v-col cols="12" md="3">
                <v-text-field
                  :value="form.sales_order_id"
                  label="Sales Order ID"
                  outlined
                  readonly
                />
              </v-col>
            </v-row>
          
            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="text-h6 font-weight-bold">
                    Partner
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.partner.name"
                          label="Partner Name"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('partner.name') }"
                          @input="clearOptionalWarning('partner.name')"
                        />
                      </v-col>
                    
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.partner.tax_number"
                          label="Tax number"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('partner.tax_number') }"
                          @input="clearOptionalWarning('partner.tax_number')"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="text-h6 font-weight-bold">
                    Contact Person
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="form.contact.name"
                          label="Name"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('contact.name') }"
                          @input="clearOptionalWarning('contact.name')"
                        />
                      </v-col>
                    
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="form.contact.phone"
                          label="Phone number"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('contact.phone') }"
                          @input="clearOptionalWarning('contact.phone')"
                        />
                      </v-col>
                    
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="form.contact.email"
                          label="Email"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('contact.email') }"
                          @input="clearOptionalWarning('contact.email')"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          
            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="text-h6 font-weight-bold">
                    Billing address
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.billing_address.name"
                          label="Name"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('billing_address.name') }"
                          @input="clearOptionalWarning('billing_address.name')"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="form.billing_address.country"
                          label="Country"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('billing_address.country') }"
                          @input="clearOptionalWarning('billing_address.country')"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="form.billing_address.zip"
                          label="Zip"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('billing_address.zip') }"
                          @input="clearOptionalWarning('billing_address.zip')"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.billing_address.city"
                          label="City"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('billing_address.city') }"
                          @input="clearOptionalWarning('billing_address.city')"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.billing_address.address"
                          label="Address"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('billing_address.address') }"
                          @input="clearOptionalWarning('billing_address.address')"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="text-h6 font-weight-bold">
                    Shipping address
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.shipping_address.name"
                          label="Name"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('shipping_address.name') }"
                          @input="clearOptionalWarning('shipping_address.name')"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="form.shipping_address.country"
                          label="Country"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('shipping_address.country') }"
                          @input="clearOptionalWarning('shipping_address.country')"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="form.shipping_address.zip"
                          label="Zip"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('shipping_address.zip') }"
                          @input="clearOptionalWarning('shipping_address.zip')"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.shipping_address.city"
                          label="City"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('shipping_address.city') }"
                          @input="clearOptionalWarning('shipping_address.city')"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.shipping_address.address"
                          label="Address"
                          outlined
                          :class="{ 'optional-warning-field': hasOptionalWarning('shipping_address.address') }"
                          @input="clearOptionalWarning('shipping_address.address')"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          
            <v-row class="mt-4">
              <v-col cols="12">
                <div class="d-flex align-center mb-3">
                  <div class="text-h6 font-weight-bold">
                    Items
                  </div>
                
                  <v-spacer />
                
                  <v-btn
                    color="primary"
                    @click="addItemDialog = true"
                    :disabled="!isEditable"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add item
                  </v-btn>
                </div>
              
                <v-alert
                  v-if="hasOptionalWarning('items')"
                  dense
                  text
                  type="warning"
                  class="mb-3"
                >
                  Some item data is still empty. Please check the product and component rows.
                </v-alert>

                <div :class="{ 'optional-warning-panel': hasOptionalWarning('items') }">
                  <delivery-note-items-tree
                    :items.sync="form.items"
                    :editable="isEditable"
                    @add-component="openAddComponentDialog"
                    @remove-parent="removeParentRow"
                    @remove-child="removeChildRow"
                    @recalculate="recalculateTotals"
                  />
                </div>
              </v-col>
            </v-row>
          
            <v-row class="mt-4">
              <v-col cols="12" md="8">
                <v-textarea
                  v-model="form.comment"
                  label="Comment"
                  rows="4"
                  outlined
                />
              </v-col>
            
              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-title class="text-h6 font-weight-bold">
                    Summary
                  </v-card-title>
                  <v-card-text>
                    <div class="d-flex justify-space-between mb-2">
                      <span>Main Item(s)</span>
                      <strong>{{ totalParentRows }}</strong>
                    </div>
                  
                    <div class="d-flex justify-space-between mb-2">
                      <span>Component(s)</span>
                      <strong>{{ totalChildRows }}</strong>
                    </div>
                  
                    <div class="d-flex justify-space-between">
                      <span>Total weight</span>
                      <strong>{{ formattedTotalWeight }} kg</strong>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      
        <v-divider />
      
        <v-card-actions class="pa-4">
          <v-btn text @click="closeDialog">
            Cancel
          </v-btn>
        
          <v-spacer />
        
          <v-btn
            color="secondary"
            outlined
            :loading="loadingPreview"
            @click="openPdfPreview"
            v-if="isEditable"
          >
            Preview
          </v-btn>
          <v-btn
            color="secondary"
            outlined
            :loading="loadingPreview"
            @click="openDeliveryNotePdf(form)"
            v-else
          >
            PDF
          </v-btn>
        
          <v-btn
            color="primary"
            outlined
            :loading="loadingSave"
            :disabled="!isEditable"
            @click="saveDraft"
          >
            Save
          </v-btn>
          
        
          <v-btn
            color="success"
            :loading="loadingIssue"
            :disabled="!canIssue"
            @click="issueDeliveryNote"
          >
            Issue
          </v-btn>
        </v-card-actions>
      </v-card>
    
      <delivery-note-add-item-dialog
        v-model="addItemDialog"
        :title="addDialogTitle"
        @selected="handleItemSelected"
      />
    
      <v-dialog v-model="previewDialog" max-width="1400px" scrollable>
        <v-card>
          <v-card-title class="headline d-flex align-center">
            <span>Delivery Note PDF Preview</span>
            <v-spacer />
            <v-btn icon @click="closePreviewDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        
          <v-divider />
        
          <v-card-text style="height: 80vh;" class="pa-0">
            <iframe
              v-if="previewUrl"
              :src="previewUrl"
              style="width: 100%; height: 100%; border: 0;"
            />
            <div
              v-else
              class="d-flex align-center justify-center"
              style="height: 100%;"
            >
              There is no preview available.
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-dialog>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import DeliveryNoteItemsTree from "./components/DeliveryNoteItemsTree.vue";
import DeliveryNoteAddItemDialog from "./components/DeliveryNoteAddItemDialog.vue";
import Swal from "sweetalert2";
import BaseSnackbarUI from "../../../components/BaseSnackbarUI.vue";


const ENDPOINT = "sales/salesOrder/delivery-note/";
const UP_ENDPOINT = "sales/salesOrder/delivery-note";

function emptyForm() {
  return {
    id: null,
    issued_by: null,
    sales_order_id: null,
    number: null,
    status: "draft",
    document_date: null,
    delivery_date: null,
    comment: null,
    internal_note: null,
    partner: {
      name: null,
      tax_number: null,
    },
    billing_address: {
      name: null,
      country: null,
      zip: null,
      city: null,
      address: null,
    },
    shipping_address: {
      name: null,
      country: null,
      zip: null,
      city: null,
      address: null,
    },
    contact: {
      name: null,
      phone: null,
      email: null,
    },
    items: [],
  };
}

export default {
  name: "DeliveryNotePlannerDialog",
  components: {
    DeliveryNoteItemsTree,
    DeliveryNoteAddItemDialog,
    BaseSnackbarUI,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogProxy: false,
      form: emptyForm(),
      loadingPlan: false,
      loadingSave: false,
      loadingIssue: false,
      loadingPreview: false,
      documentDateMenu: false,
      deliveryDateMenu: false,

      addItemDialog: false,
      addMode: "parent",
      selectedParentIndex: null,

      previewDialog: false,
      previewUrl: null,

      uSnackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
      optionalWarnings: {},
    };
  },
  computed: {
    isEditMode() {
      return !!this.form.id;
    },
    isEditable() {
      return ["draft", "planned"].includes(this.form.status);
    },
    canIssue() {
      return this.isEditable && this.form.items.length > 0 && !this.loadingIssue;
    },
    totalParentRows() {
      return this.form.items.length;
    },
    totalChildRows() {
      return this.form.items.reduce((sum, row) => {
        return sum + ((row.children || []).length);
      }, 0);
    },
    totalWeight() {
      return this.form.items.reduce((sum, row) => {
        const parentWeight = (Number(row.quantity) || 0) * (Number(row.num_of_pcs) || 0) * (Number(row.weight_per_unit) || 0);
        const childWeight = (row.children || []).reduce((childSum, child) => {
          return childSum + ((Number(child.quantity) || 0) * (Number(child.num_of_pcs) || 0) * (Number(child.weight_per_unit) || 0));
        }, 0);

        return sum + parentWeight + childWeight;
      }, 0);
    },
    formattedTotalWeight() {
      return Number(this.totalWeight).toFixed(2);
    },
    addDialogTitle() {
      return this.addMode === "component" ? "Komponens hozzáadása" : "Termék hozzáadása";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.dialogProxy = val;
      },
    },
    dialogProxy(val) {
      this.$emit("input", val);
    },
  },
  methods: {
    async openForSalesOrder(salesOrderId) {
      this.resetForm();
      this.dialogProxy = true;
      this.loadingPlan = true;

      try {
        const { data } = await ApiService.get(ENDPOINT + "plan", salesOrderId);
        this.form = this.normalizePlan(data);
        this.optionalWarnings = {};
        this.showSnackbar("Plan load successfully ", 'success', 5000);
      } catch (error) {
        this.showSnackbar(error?.response?.data?.message ?? "Plan load error", 'error', 5000);
        console.error("Plan load error", error);
      } finally {
        this.loadingPlan = false;
      }
    },

    async openExisting(deliveryNoteId) {
      this.resetForm();
      this.dialogProxy = true;
      this.loadingPlan = true;

      try {
        const { data } = await ApiService.get(ENDPOINT + deliveryNoteId);
        this.form = this.normalizeExisting(data);
        this.optionalWarnings = {};
        this.showSnackbar("Delivery note's data loaded successfully ", 'success', 5000);
      } catch (error) {
        this.showSnackbar("Delivery note's data load error " + error?.response, 'error', 5000);
        console.error("Delivery note load error", error);
      } finally {
        this.loadingPlan = false;
      }
    },

    normalizePlan(data) {
      return {
        id: null,
        issued_by: data.issued_by || null,
        sales_order_id: data.sales_order_id,
        number: null,
        status: "draft",
        document_date: data.document_date,
        delivery_date: data.delivery_date,
        comment: data.comment,
        internal_note: data.internal_note,
        partner: data.partner || { name: null, tax_number: null },
        billing_address: data.billing_address || {},
        shipping_address: data.shipping_address || {},
        contact: data.contact || {},
        items: (data.items || []).map(this.normalizeParentRow),
      };
    },

    normalizeExisting(data) {
      return {
        id: data.id,
        issued_by: data.issued_by,
        sales_order_id: data.sales_order_id,
        number: data.number,
        status: data.status,
        document_date: data.document_date,
        delivery_date: data.delivery_date,
        comment: data.comment,
        internal_note: data.internal_note,
        partner: {
          name: data.partner_name,
          tax_number: data.partner_tax_number,
        },
        billing_address: {
          name: data.billing_name,
          country: data.billing_country,
          zip: String(data.billing_zip),
          city: data.billing_city,
          address: data.billing_address,
        },
        shipping_address: {
          name: data.shipping_name,
          country: data.shipping_country,
          zip: String(data.shipping_zip),
          city: data.shipping_city,
          address: data.shipping_address,
        },
        contact: {
          name: data.contact_name,
          phone: data.contact_phone,
          email: data.contact_email,
        },
        items: (data.items || []).map(this.normalizeExistingParentRow),
        pdf_path: data.pdf_path,
      };
    },

    normalizeParentRow(row) {
      return {
        local_key: this.uid(),
        sales_order_item_id: row.sales_order_item_id || null,
        item_id: row.item_id || null,
        parent_id: null,
        line_type: row.line_type || "product",
        sort_order: row.sort_order || 10,
        name: row.name || null,
        reference: row.reference || null,
        unit: row.unit || "db",
        quantity: Number(row.quantity || 0),
        num_of_pcs: Number(row.num_of_pcs || row.quantity || 0),
        weight_per_unit: Number(row.weight_per_unit || 0),
        is_from_order: !!row.is_from_order,
        is_auto_component: false,
        comment: row.comment || null,
        children: (row.children || []).map(this.normalizeChildRow),
      };
    },

    normalizeExistingParentRow(row) {
      return {
        local_key: this.uid(),
        sales_order_item_id: row.sales_order_item_id || null,
        item_id: row.item_id || null,
        parent_id: null,
        line_type: row.line_type || "product",
        sort_order: row.sort_order || 10,
        name: row.name_snapshot || row.name || null,
        reference: row.reference_snapshot || row.reference || null,
        unit: row.unit_snapshot || row.unit || "db",
        quantity: Number(row.quantity || 0),
        num_of_pcs: Number(row.num_of_pcs || row.quantity || 0),
        weight_per_unit: Number(row.weight_per_unit_snapshot || row.weight_per_unit || 0),
        is_from_order: !!row.is_from_order,
        is_auto_component: false,
        comment: row.comment || null,
        children: (row.children || []).map(this.normalizeExistingChildRow),
      };
    },

    normalizeChildRow(row) {
      return {
        local_key: this.uid(),
        sales_order_item_id: null,
        item_id: row.item_id || null,
        parent_id: null,
        line_type: row.line_type || "component",
        sort_order: row.sort_order || 11,
        name: row.name || null,
        reference: row.reference || null,
        unit: row.unit || "db",
        quantity: Number(row.quantity || 0),
        num_of_pcs: Number(row.num_of_pcs || row.quantity || 0),
        weight_per_unit: Number(row.weight_per_unit || 0),
        is_from_order: false,
        is_auto_component: !!row.is_auto_component,
        component_relation_quantity: row.component_relation_quantity || null,
        component_relation_num_of_pcs: row.component_relation_num_of_pcs || null,
        comment: row.comment || null,
      };
    },

    normalizeExistingChildRow(row) {
      return {
        local_key: this.uid(),
        sales_order_item_id: null,
        item_id: row.item_id || null,
        parent_id: row.parent_id || null,
        line_type: row.line_type || "component",
        sort_order: row.sort_order || 11,
        name: row.name_snapshot || row.name || null,
        reference: row.reference_snapshot || row.reference || null,
        unit: row.unit_snapshot || row.unit || "db",
        quantity: Number(row.quantity || 0),
        num_of_pcs: Number(row.num_of_pcs || row.quantity || 0),
        weight_per_unit: Number(row.weight_per_unit_snapshot || row.weight_per_unit || 0),
        is_from_order: false,
        is_auto_component: !!row.is_auto_component,
        component_relation_quantity: row.component_relation_quantity || null,
        component_relation_num_of_pcs: row.component_relation_num_of_pcs || null,
        comment: row.comment || null,
      };
    },

    preparePayload() {
      this.normalizeSortOrders();
    
      return {
        sales_order_id: this.form.sales_order_id,
        document_date: this.form.document_date,
        delivery_date: this.form.delivery_date,
        comment: this.form.comment,
        internal_note: this.form.internal_note,
        partner: this.form.partner,
        billing_address: this.form.billing_address,
        shipping_address: this.form.shipping_address,
        contact: this.form.contact,
        items: this.form.items.map((row, parentIndex) => ({
          sales_order_item_id: row.sales_order_item_id,
          item_id: row.item_id,
          line_type: row.line_type,
          sort_order: row.sort_order || ((parentIndex + 1) * 10),
          name: row.name,
          reference: row.reference,
          unit: row.unit,
          quantity: Number(row.quantity || 0),
          num_of_pcs: Number(row.num_of_pcs || 0),
          weight_per_unit: Number(row.weight_per_unit || 0),
          is_from_order: !!row.is_from_order,
          comment: row.comment,
          children: (row.children || []).map((child, childIndex) => ({
            item_id: child.item_id,
            line_type: child.line_type || "component",
            sort_order: child.sort_order || (((parentIndex + 1) * 10) + childIndex + 1),
            name: child.name,
            reference: child.reference,
            unit: child.unit,
            quantity: Number(child.quantity || 0),
            num_of_pcs: Number(child.num_of_pcs || 0),
            weight_per_unit: Number(child.weight_per_unit || 0),
            is_auto_component: !!child.is_auto_component,
            component_relation_quantity: child.component_relation_quantity,
            component_relation_num_of_pcs: child.component_relation_num_of_pcs,
            comment: child.comment,
          })),
        })),
      };
    },

    isEmpty(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    getValueByPath(path) {
      return path.split(".").reduce((current, key) => {
        if (current === null || current === undefined) {
          return null;
        }

        return current[key];
      }, this.form);
    },

    hasOptionalWarning(field) {
      return Boolean(this.optionalWarnings[field]);
    },

    clearOptionalWarning(field) {
      if (this.optionalWarnings[field]) {
        this.$delete(this.optionalWarnings, field);
      }
    },

    onDocumentDateSelected() {
      this.documentDateMenu = false;
      this.clearOptionalWarning("document_date");
    },

    onDeliveryDateSelected() {
      this.deliveryDateMenu = false;
      this.clearOptionalWarning("delivery_date");
    },

    getItemOptionalWarningKey(parentIndex, field) {
      return `items.${parentIndex}.${field}`;
    },

    getChildOptionalWarningKey(parentIndex, childIndex, field) {
      return `items.${parentIndex}.children.${childIndex}.${field}`;
    },

    getMissingOptionalFields() {
      const missingFields = [];
      const optionalFields = [
        { key: "document_date", label: "Issue date" },
        { key: "delivery_date", label: "Delivery date" },
        { key: "partner.name", label: "Partner - Name" },
        { key: "partner.tax_number", label: "Partner - Tax number" },
        { key: "contact.name", label: "Contact Person - Name" },
        { key: "contact.phone", label: "Contact Person - Phone number" },
        { key: "contact.email", label: "Contact Person - Email" },
        { key: "billing_address.name", label: "Billing address - Name" },
        { key: "billing_address.country", label: "Billing address - Country" },
        { key: "billing_address.zip", label: "Billing address - Zip" },
        { key: "billing_address.city", label: "Billing address - City" },
        { key: "billing_address.address", label: "Billing address - Address" },
        { key: "shipping_address.name", label: "Shipping address - Name" },
        { key: "shipping_address.country", label: "Shipping address - Country" },
        { key: "shipping_address.zip", label: "Shipping address - Zip" },
        { key: "shipping_address.city", label: "Shipping address - City" },
        { key: "shipping_address.address", label: "Shipping address - Address" },
      ];

      optionalFields.forEach((field) => {
        if (this.isEmpty(this.getValueByPath(field.key))) {
          missingFields.push(field);
        }
      });

      if (Array.isArray(this.form.items)) {
        this.form.items.forEach((item, parentIndex) => {
          [
            { key: "name", label: "Name" },
            { key: "reference", label: "Reference" },
            { key: "unit", label: "Unit" },
            { key: "quantity", label: "Quantity" },
            { key: "num_of_pcs", label: "Number of pieces" },
            { key: "weight_per_unit", label: "Weight per unit" },
          ].forEach((field) => {
            if (this.isEmpty(item[field.key])) {
              missingFields.push({
                key: this.getItemOptionalWarningKey(parentIndex, field.key),
                label: `Item ${parentIndex + 1} - ${field.label}`,
              });
            }
          });

          (item.children || []).forEach((child, childIndex) => {
            [
              { key: "name", label: "Name" },
              { key: "reference", label: "Reference" },
              { key: "unit", label: "Unit" },
              { key: "quantity", label: "Quantity" },
              { key: "num_of_pcs", label: "Number of pieces" },
              { key: "weight_per_unit", label: "Weight per unit" },
            ].forEach((field) => {
              if (this.isEmpty(child[field.key])) {
                missingFields.push({
                  key: this.getChildOptionalWarningKey(parentIndex, childIndex, field.key),
                  label: `Item ${parentIndex + 1} / Component ${childIndex + 1} - ${field.label}`,
                });
              }
            });
          });
        });
      }

      return missingFields;
    },

    markOptionalWarnings(fields) {
      const warnings = {};

      fields.forEach((field) => {
        warnings[field.key] = true;

        if (field.key.indexOf("items.") === 0) {
          warnings.items = true;
        }
      });

      this.optionalWarnings = warnings;
    },

    async confirmMissingOptionalFields() {
      const missingFields = this.getMissingOptionalFields();

      if (!missingFields.length) {
        this.optionalWarnings = {};
        return true;
      }

      const fieldList = missingFields
        .map((field) => `<li>${field.label}</li>`)
        .join("");

      const result = await Swal.fire({
        title: "Some fields are empty",
        icon: "warning",
        html: `The following fields are not filled:<br><ul style="text-align:left;margin-top:12px;">${fieldList}</ul><br>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Yes, save the delivery note",
        cancelButtonText: "No, I will fill them",
        confirmButtonColor: "#00c300",
        width: "650px",
      });

      if (result.isConfirmed) {
        this.optionalWarnings = {};
        return true;
      }

      this.markOptionalWarnings(missingFields);
      return false;
    },

    async saveDraft() {
        this.loadingSave = true;

      try {
        const payload = this.preparePayload();
            let response;

        if (this.form.id) {
          response = await ApiService.update(UP_ENDPOINT, this.form.id, payload);
        } else {
          response = await ApiService.post(ENDPOINT, payload);
        }

            const note = response.data.delivery_note || response.data;

        this.form = this.normalizeExisting(note);
            this.recalculateTotals();

        this.showSnackbar(response?.data?.message ?? 'Delivery note saved successfully', 'success', 5000);
        this.$emit("saved", note);
      } catch (error) {
        this.showSnackbar(error?.response?.data?.message ?? "Delivery note issue error", 'error', 5000);
        console.error("Delivery note save error", error);
      } finally {
        this.loadingSave = false;
      }
    },

    async issueDeliveryNote() {
      if (!this.form.items.length) {
        await Swal.fire({
          title: "Missing items",
          icon: "warning",
          text: "Please add at least one item before creating the delivery note.",
          confirmButtonText: "OK",
        });

        return;
      }

      const missingFields = this.getMissingOptionalFields();

      this.markOptionalWarnings(missingFields);

      const canContinueWithEmptyFields = await this.confirmMissingOptionalFields();

      if (!canContinueWithEmptyFields) {
        return;
      }

      if (!this.form.id) {
        await this.saveDraft();
      }
    
      if (!this.form.id) {
        return;
      }
    
      const result = await Swal.fire({
        title: "<strong>ALERT</strong>",
        icon: "warning",
        width: "600px",
        text: "Are you sure you want to create the delivery note? This action cannot be undone.",
        showCloseButton: true,
        focusConfirm: false,
        confirmButtonText: "Yes, create it!",
        confirmButtonColor: "#00c300",
        showCancelButton: true,
      });
    
      if (!result.isConfirmed) {
        return;
      }
    
      this.loadingIssue = true;
    
      try {
        const { data } = await ApiService.post(ENDPOINT + this.form.id + "/issue", {});
        const note = data.delivery_note || data;
      
        this.form = this.normalizeExisting(note);
        this.showSnackbar(data?.data?.message ?? "Delivery note issued successfully", 'success', 5000);
      
        this.$emit("issued", note);
      } catch (error) {
        this.showSnackbar(error?.response?.data?.message ?? "Delivery note issue error", 'error', 5000);
        console.error("Delivery note issue error", error);
      } finally {
        this.loadingIssue = false;
      }
    },

    openAddComponentDialog(parentIndex) {
      this.addMode = "component";
      this.selectedParentIndex = parentIndex;
      this.addItemDialog = true;
    },

    handleItemSelected(item) {
      if (!item) {
        return;
      }

      if (this.addMode === "component" && this.selectedParentIndex !== null) {
        this.form.items[this.selectedParentIndex].children.push({
          local_key: this.uid(),
          sales_order_item_id: null,
          item_id: item.id,
          parent_id: null,
          line_type: "component",
          sort_order: null,
          name: item.name,
          reference: item.reference,
          unit: item.unit || "db",
          quantity: 1,
          num_of_pcs: 1,
          weight_per_unit: Number(item.weight || item.gross_weight || 0),
          is_from_order: false,
          is_auto_component: false,
          component_relation_quantity: null,
          component_relation_num_of_pcs: null,
          comment: null,
        });
      } else {
        this.form.items.push({
          local_key: this.uid(),
          sales_order_item_id: null,
          item_id: item.id,
          parent_id: null,
          line_type: "product",
          sort_order: null,
          name: item.name,
          reference: item.reference,
          unit: item.unit || "db",
          quantity: 1,
          num_of_pcs: 1,
          weight_per_unit: Number(item.weight || item.gross_weight || 0),
          is_from_order: false,
          is_auto_component: false,
          comment: null,
          children: [],
        });
      }

      this.addMode = "parent";
      this.selectedParentIndex = null;
      this.clearOptionalWarning("items");
      this.recalculateTotals();
    },

    removeParentRow(parentIndex) {
      this.form.items.splice(parentIndex, 1);
      this.clearOptionalWarning("items");
      this.recalculateTotals();
    },

    removeChildRow(payload) {
      const { parentIndex, childIndex } = payload;
      this.form.items[parentIndex].children.splice(childIndex, 1);
      this.clearOptionalWarning("items");
      this.recalculateTotals();
    },

    recalculateTotals() {
      this.form.items.forEach(parent => {
        this.updateAutoComponents(parent);
      });
    
      this.form.items = [...this.form.items];
    },

    resolveStatusColor(status) {
      switch (status) {
        case "draft":
          return "grey";
        case "planned":
          return "orange";
        case "issued":
          return "success";
        case "cancelled":
          return "error";
        default:
          return "primary";
      }
    },

    closeDialog() {
      this.dialogProxy = false;
      this.previewDialog = false;
      this.cleanupPreviewUrl();
      this.$emit("closed");
      this.resetForm();
    },

    resetForm() {
      this.cleanupPreviewUrl();
      this.form = emptyForm();
      this.optionalWarnings = {};
      this.addMode = "parent";
      this.selectedParentIndex = null;
    },

    uid() {
      return "dn_" + Math.random().toString(36).substr(2, 9);
    },

    updateAutoComponents(parentRow) {
      (parentRow.children || []).forEach(child => {
        if (child.is_auto_component) {
          if (child.component_relation_quantity) {
            child.quantity =
              Number(parentRow.quantity || 0) *
              Number(child.component_relation_quantity || 0);
          }
        
          if (child.component_relation_num_of_pcs) {
            child.num_of_pcs =
              Number(parentRow.num_of_pcs || 0) *
              Number(child.component_relation_num_of_pcs || 0);
          }
        }
      });
    },

    normalizeSortOrders() {
        this.form.items.forEach((parent, parentIndex) => {
        parent.sort_order = (parentIndex + 1) * 10;

            (parent.children || []).forEach((child, childIndex) => {
            child.sort_order = parent.sort_order + childIndex + 1;
            });
        });
    },

    async openPdfPreview() {
      this.loadingPreview = true;
        
      try {
        const payload = this.preparePayload();
    
        const response = await ApiService.post(
          "sales/salesOrder/delivery-note/preview",
          payload,
          { responseType: "blob" }
        );
    
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
        }
    
        const blob = new Blob([response.data], { type: "application/pdf" });
        this.previewUrl = URL.createObjectURL(blob);
        this.previewDialog = true;

      } catch (error) {
        this.showSnackbar(error?.response?.data?.message ?? "PDF preview error", 'error', 5000);
        console.error("PDF preview error", error);
      } finally {
        this.loadingPreview = false;
      }
    },
    
    closePreviewDialog() {
      this.previewDialog = false;
    },
    
    cleanupPreviewUrl() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },

    openDeliveryNotePdf(note) {
      console.log(note);
      if (!note || !note.id) return;


      const url =
        (process.env.VUE_APP_BACKEND_URL || "").replace(/\/$/, "") +
        "/public/" + note.pdf_path;
      
      window.open(url, "_blank");
    },
    showSnackbar(text, color = "success", timeout = 3000) {
      this.uSnackbar.text = text;
      this.uSnackbar.color = color;
      this.uSnackbar.timeout = timeout;
      this.uSnackbar.show = true;
    },
  },
};
</script>

<style scoped>
.optional-warning-field >>> .v-input__slot {
  background-color: #fff8e1 !important;
}

.optional-warning-field >>> fieldset {
  border-color: #f9a825 !important;
}

.optional-warning-panel {
  background-color: #fff8e1;
  border: 1px solid #f9a825;
  border-radius: 4px;
  padding: 8px;
}
</style>
